import { useState, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { MessageProps } from "../components/Chat/Message";
import { sendChatMessage } from "../services/chatService";

export const useChat = () => {
  const [messages, setMessages] = useState<MessageProps[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [previousResponseId, setPreviousResponseId] = useState<string | null>(
    null,
  );

  const auth = useAuth();
  const token = auth.user?.id_token;

  const handleSend = useCallback(async () => {
    if (!inputMessage.trim() || !token) return;

    // Add user message
    const userMessage: MessageProps = {
      id: Date.now().toString(),
      content: inputMessage,
      isBot: false,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // Make API request with the correct parameters
      const data = await sendChatMessage(
        inputMessage,
        token,
        previousResponseId,
      );

      // Store responseId for next message
      if (data.responseId) {
        setPreviousResponseId(data.responseId);
      }

      // Add bot message
      const botMessage: MessageProps = {
        id: Date.now().toString(),
        content:
          data.content ||
          data.response ||
          data.message ||
          "I'm sorry, I couldn't generate a response. Please try again.",
        isBot: true,
        timestamp: new Date(),
        responseId: data.responseId, // Store the responseId with the message
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Error fetching response:", error);

      // Add error message
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          content:
            "Sorry, there was an error processing your request. Please try again.",
          isBot: true,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, token, previousResponseId]);

  return {
    messages,
    inputMessage,
    isLoading,
    setInputMessage,
    handleSend,
  };
};
